<template>
  <div id="articles-section">
    <header
      id="articles-section-header"
      class="px-5 py-4 m-0 bg-secondary d-flex"
    >
      <h6 class="m-0">
        Articles List
      </h6>
      <div
        class="ml-auto"
        data-test="articles-headers-actions"
        v-show="selectedTab !== 'failed-articles'"
      >
        <button
          id="reset-filters-button"
          v-show="filtersQuery !== ''"
          data-test="reset-filters-button"
          data-testid="reset-filters-button"
          @click="resetFilters()"
        >
          Reset
          <i class="fa-light fa-x" />
          <i class="fa-light fa-x" />
        </button>
        <button
          class="btn btn-secondary btn-sm"
          data-test="filters-section-button"
          @click="displayFilters()"
        >
          Filters
          <i class="fal fa-search" />
        </button>
        <articles-actions
          v-if="GET_IS_MANAGED_CAMPAIGN || (GET_IS_SELF_SERVICE_CAMPAIGN && !isViewType('Setup'))"
        />
      </div>
    </header>
    <articles-filters
      v-show="displayArticlesFilters"
      id="articles-filters-section"
      ref="articles-filters"
      data-test="articles-filters-section"
      @filtersApplied="filtersApplied"
    />
    <div>
      <ul class="nav nav-tabs bg-secondary">
        <li
          :class="['nav-item', {'full-width': hasFailedArticles === false}]"
        >
          <button
            data-test="successfully-scraped-content-items-tab"
            :class="['nav-link justify-content-center', {'active': selectedTab === 'scraped-articles'}]"
            @click="setSelectedTab('scraped-articles')"
          >
            Uploaded ({{ totalScrapedArticlesLength }})
          </button>
        </li>
        <li
          v-if="hasFailedArticles"
          class="nav-item"
        >
          <button
            data-test="failed-scraped-content-items-tab"
            :class="['nav-link justify-content-center', { 'active': selectedTab === 'failed-articles' }]"
            @click="setSelectedTab('failed-articles')"
          >
            Failed (<span class="text-danger">{{ failedArticlesLength }}</span>)
          </button>
        </li>
      </ul>
    </div>
    <div id="articles-container">
      <div
        v-if="loadingArticles === true"
        class="mx-auto mt-5"
        :class="{loader: loadingArticles}"
      />
      <div
        v-show="loadingArticles === false"
        class="h-100 mh-100 overflow-auto"
      >
        <upload-progress-animation-box />
        <successfully-scraped-content-items
          v-show="selectedTab === 'scraped-articles'"
          data-test="successfully-scraped-content-items"
        />
        <failed-scraped-content-items
          v-if="hasFailedArticles"
          v-show="selectedTab === 'failed-articles'"
          data-test="failed-scraped-content-items"
        />
        <content-item-card
          v-show="selectedTab === 'scraped-articles'"
          v-for="(article, index) in inProgressArticles"
          :key="index"
          :index="index"
          :article="article"
          :data-test="`in-progress-content-item-${index}`"
        />
      </div>
    </div>
    <div
      class="list-item-pagination border-top"
      v-if="showPagination"
    >
      <base-pagination
        class="pt-3"
        :items-count="allArticlesLength"
        :current-page.sync="currentPage"
        :per-page-selected.sync="perPageSelected"
        items-name="Items"
        :hide-page-select="true"
        @pagingUpdate="updateArticlesList"
      />
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import ArticlesFilters from '@/modules/content/components/Articles/ArticlesFilters.vue';
import ArticlesActions from '@/modules/content/components/Articles/ArticlesActions.vue';
import SuccessfullyScrapedContentItems from '@/modules/content/components/Articles/SuccessfullyScrapedContentItems.vue';
import FailedScrapedContentItems from '@/modules/content/components/Articles/FailedScrapedContentItems.vue';
import ContentItemCard from '@/modules/content/components/Articles/ContentItemCard.vue';
import UploadProgressAnimationBox from '@/modules/content/components/Articles/UploadProgressAnimationBox.vue';
import BasePagination from '@/components/BasePagination.vue';


export default {
  name: 'ArticlesSection',
  components: {
    ArticlesFilters,
    UploadProgressAnimationBox,
    ArticlesActions,
    SuccessfullyScrapedContentItems,
    FailedScrapedContentItems,
    ContentItemCard,
    BasePagination,
  },
  data() {
    return {
      displayArticlesFilters: false,
      selectedTab: 'scraped-articles',
      currentPage: 1,
      perPageSelected: 30,
    };
  },
  methods: {
    ...mapActions('content', {
      fetchArticles: contentTypes.FETCH_ARTICLES,
      setFiltersQuery: contentTypes.SET_FILTERS_QUERY,
    }),
    articlesFiltersElement() {
      return this.$refs['articles-filters']?.$el;
    },
    displayFilters() {
      const element = this.articlesFiltersElement();
      if (element?.classList?.value.includes('open-filters')) return this.closeFiltersAnimation();
      this.displayArticlesFilters = true;
      this.openFiltersAnimation();
    },
    openFiltersAnimation() {
      const element = this.articlesFiltersElement();
      element?.classList.add('open-filters');
    },
    filtersApplied(applied) {
      if (applied === true) this.closeFiltersAnimation();
    },
    closeFiltersAnimation() {
      const element = this.articlesFiltersElement();
      if (element?.classList?.value.includes('open-filters') === false) return;
      element?.classList.add('close-filters');
      element?.addEventListener('animationend', () => {
        element?.classList.remove('open-filters');
        element?.classList.remove('close-filters');
        this.displayArticlesFilters = false;
      }, { once: true });
    },
    resetFilters() {
      this.setFiltersQuery('');
      this.closeFiltersAnimation();
      this.getArticles();
    },
    setSelectedTab(tab) {
      this.selectedTab = tab;
    },
    updateArticlesList(payload) {
      this.fetchArticles({ page: payload.page, pageSize: payload.perPageSelected });
    },
    getArticles() {
      if (this.isViewType('Editor')) {
        this.fetchArticles({ page: this.currentPage, pageSize: this.perPageSelected });
      } else {
        this.fetchArticles();
      }
    },
  },
  computed: {
    ...mapState('content', [
      'inProgressContentScrapes',
      'newScrapedContent',
      'filtersQuery',
      'loadingArticles',
      'totalItems',
    ]),
    ...mapGetters('content', {
      inProgressArticles: contentTypes.GET_IN_PROGRESS_CONTENT_ITEMS,
      scrapedArticles: contentTypes.GET_SUCCESSFULLY_SCRAPED_CONTENT_ITEMS,
      failedArticles: contentTypes.GET_FAILED_SCRAPED_CONTENT_ITEMS,
    }),
    ...mapGetters('campaigns', {
      isViewType: campaignTypes.GET_IS_VIEW_TYPE,
    }),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_SELF_SERVICE_CAMPAIGN,
    ]),
    allArticlesLength() {
      return this.newScrapedContent?.length;
    },
    totalScrapedArticlesLength() {
      return this.totalItems;
    },
    failedArticlesLength() {
      return this.failedArticles?.length;
    },
    hasFailedArticles() {
      return this.failedArticles?.length !== 0;
    },
    autoSelectScrapedTab() {
      return this.hasFailedArticles === false ? this.setSelectedTab('scraped-articles') : null;
    },
    showPagination() {
      return this.selectedTab === 'scraped-articles' && (this.isViewType('Editor') || this.isViewType('Showcase'));
    },
  },
  created() {
    this.getArticles();
  },
};
</script>
<style lang="scss">
#articles-section-header {
  position: relative;
  height: 58px;
}
</style>
<style lang="scss" scoped>
#articles-section {
  height: 100%;
}
#articles-container {
  height: 100%;
  max-height: calc(100% - 124.2px);
}
#reset-filters-button {
  all: unset;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  margin-right: 12px;
}

.nav-tabs{
  height: 72px;
  margin: 0;
  position: relative !important;
}
.nav-item {
  width: 50%;
  height: 56px;
  padding: 0;
  position: absolute !important;
  bottom: 0;
  left: 0;
}
.full-width {
  width: 100% !important;
}
.nav-item:nth-child(2) {
  left: 50%;
}
.nav-link {
  height: 52px;
  width: 100%;
  border-radius: 15px 15px 0px 0px;
  position: absolute;
  bottom: 0px;
  left: 0px;
}
#articles-filters-section {
  height: 0px;
  overflow: hidden;
}
#articles-filters-section.open-filters {
  animation-name: openFilters;
  animation-duration: .5s;
  animation-fill-mode: forwards;
}
#articles-filters-section.close-filters {
  animation-name: closeFilters;
  animation-duration: .5s;
  animation-fill-mode: forwards;
}
@keyframes openFilters {
  from {
    height: 0px;
  }
  to {
    height: 370px;
  }
}

@keyframes closeFilters {
  from {
    height: 370px;
  }
  to {
    height: 0px;
  }
}
.list-item-pagination {
  padding: 0px 12px;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 2;
}
</style>
